import { zodResolver } from '@hookform/resolvers/zod';
import {
  cisVerificationInputSchema,
  utrSchema,
  type CisVerificationInput,
  type CisVerificationResult,
  type Utr,
} from '@pgs/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { verifyCis } from '../api';
import { focusFirstError } from '../focusFirstError';
import { NavButtons } from '../NavButtons';
import { StepPanel } from '../StepPanel';
import type { RegistrationDraft } from '../types';
import { TextField } from '@/components/form/TextField';
import { RadioGroup } from '@/components/form/RadioGroup';

type EntityType = 'soleTrader' | 'company';

// Company verification isn't wired in the HMRC bot yet — restrict to sole trader for now.
// Flip to true (and wire the bot's company branch) to re-enable the company option.
const COMPANY_VERIFY_ENABLED = false;

interface Props {
  defaultValues?: Partial<Utr>;
  personal: RegistrationDraft['personal'];
  verification?: RegistrationDraft['cisVerification'];
  onBack: () => void;
  onSubmit: (values: Utr) => void;
  onVerified: (v: RegistrationDraft['cisVerification']) => void;
}

const TREATMENT_LABEL: Record<CisVerificationResult['taxTreatment'], string> = {
  gross: 'gross',
  net: 'net',
  higher: 'higher',
};

export const Step5Utr = ({
  defaultValues,
  personal,
  verification,
  onBack,
  onSubmit,
  onVerified,
}: Props) => {
  const form = useForm<Utr>({
    resolver: zodResolver(utrSchema),
    defaultValues: defaultValues ?? { mode: 'enter', number: '' },
    mode: 'onBlur',
  });
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = form;
  const mode = watch('mode');
  const number = mode === 'enter' ? (watch('number') ?? '').trim() : '';

  // Entity type + the names that drive verification live outside the utrSchema form — they are
  // a verification concern, not part of the persisted registration. Pre-fill the sole-trader
  // name from personal details; keep it editable (HMRC's matched name can differ).
  const [entityType, setEntityType] = useState<EntityType>(verification?.entityType ?? 'soleTrader');
  const [firstName, setFirstName] = useState(personal.forename ?? '');
  const [lastName, setLastName] = useState(personal.surname ?? '');
  const [niNumber, setNiNumber] = useState(personal.niNumber ?? '');
  const [companyName, setCompanyName] = useState(verification?.companyName ?? '');
  const [result, setResult] = useState<CisVerificationResult | undefined>(verification?.result);

  const buildInput = (): CisVerificationInput =>
    entityType === 'company'
      ? { entityType: 'company', utr: number, companyName: companyName.trim() }
      : {
          entityType: 'soleTrader',
          utr: number,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          // Optional — HMRC matches better with the NI number alongside the UTR.
          ...(niNumber.trim() ? { niNumber: niNumber.trim() } : {}),
        };

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const parsed = cisVerificationInputSchema.safeParse(buildInput());
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Please complete the details above');
      }
      return verifyCis(parsed.data);
    },
    onSuccess: (res) => {
      setResult(res);
      onVerified({
        entityType,
        companyName: entityType === 'company' ? companyName.trim() : undefined,
        result: res,
      });
    },
  });

  const detailsReady =
    /^\d{10}$/.test(number) &&
    (entityType === 'company' ? companyName.trim().length > 0 : firstName.trim() && lastName.trim());

  return (
    <StepPanel
      icon="fa-id-card"
      title="Your CIS UTR"
      description="We need your Unique Tax Reference (UTR) to check your status under the Construction Industry Scheme. If you don't have one yet, select N/A."
    >
      <form onSubmit={handleSubmit(onSubmit, focusFirstError)}>
        <label className="lbl" style={{ marginTop: 0 }}>
          What Is Your CIS UTR?
        </label>
        <div className="cis-note">
          This stands for 'Construction Industry Scheme' 'Unique Tax Reference' number.
        </div>
        <RadioGroup
          control={control}
          name="mode"
          options={[
            { value: 'enter', label: 'Enter My UTR' },
            { value: 'na', label: 'N/A' },
          ]}
        />
        {mode === 'enter' && (
          <div className="mt-3">
            <TextField
              {...register('number')}
              inputMode="numeric"
              placeholder="Enter your 10-digit UTR"
              error={mode === 'enter' ? (errors as { number?: { message?: string } }).number?.message : undefined}
            />

            <div className="mt-4 rounded-sm border border-ink-100 p-4">
              {COMPANY_VERIFY_ENABLED && (
                <>
                  <label className="lbl" style={{ marginTop: 0 }}>
                    Who is registering for CIS?
                  </label>
                  <RadioGroupLocal
                    value={entityType}
                    onChange={(v) => {
                      setEntityType(v);
                      setResult(undefined);
                    }}
                  />
                </>
              )}

              {entityType === 'soleTrader' ? (
                <>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="First name"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setResult(undefined);
                      }}
                      placeholder="As registered with HMRC"
                    />
                    <TextField
                      label="Last name"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setResult(undefined);
                      }}
                      placeholder="As registered with HMRC"
                    />
                  </div>
                  <div className="mt-3">
                    <TextField
                      label="National Insurance number (optional)"
                      value={niNumber}
                      onChange={(e) => {
                        setNiNumber(e.target.value);
                        setResult(undefined);
                      }}
                      placeholder="e.g. QQ123456C"
                    />
                    <p className="mt-1 text-[11px] text-ink-400">
                      Providing this alongside your UTR improves the chance of a successful HMRC
                      match.
                    </p>
                  </div>
                </>
              ) : (
                <div className="mt-3">
                  <TextField
                    label="Company name"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      setResult(undefined);
                    }}
                    placeholder="Registered company name"
                  />
                </div>
              )}

              <button
                type="button"
                disabled={!detailsReady || verifyMutation.isPending}
                onClick={() => verifyMutation.mutate()}
                className="mt-3 rounded-sm bg-navy px-4 py-3 text-left text-[11px] font-bold uppercase leading-snug tracking-wider text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {verifyMutation.isPending
                  ? 'Verifying with HMRC…'
                  : 'Verify my CIS status with HMRC'}
              </button>

              {verifyMutation.isPending && (
                <p className="mt-2 text-[11px] font-semibold text-ink-500">
                  Checking with HMRC — this can take up to 2 minutes. Please don't close this page.
                </p>
              )}
              {verifyMutation.isError && (
                <p className="mt-2 text-[11px] font-semibold text-red-600">
                  {(verifyMutation.error as Error).message ||
                    'Verification could not be completed. You can try again or continue without verifying.'}
                </p>
              )}
              {result && !verifyMutation.isPending && (
                <p
                  className={`mt-2 text-[11px] font-bold ${
                    result.matchStatus === 'matched' ? 'text-teal-dark' : 'text-amber-600'
                  }`}
                >
                  {result.matchStatus === 'matched' ? 'Matched' : 'Not matched'} — {result.taxRate}% (
                  {TREATMENT_LABEL[result.taxTreatment]})
                  {result.verificationNumber ? ` · ${result.verificationNumber}` : ''}
                </p>
              )}
              <p className="mt-2 text-[11px] text-ink-400">
                Verification is optional — you can continue without it (HMRC treats unverified
                subcontractors at the 30% rate).
              </p>
            </div>
          </div>
        )}
        <NavButtons onBack={onBack} submit />
      </form>
    </StepPanel>
  );
};

// Local sole-trader/company selector. Kept out of the react-hook-form (utrSchema) so the
// persisted UTR shape is untouched; uses the same inline radio styling as RadioGroup.
const RadioGroupLocal = ({
  value,
  onChange,
}: {
  value: EntityType;
  onChange: (v: EntityType) => void;
}) => (
  <div className="mt-2 flex flex-wrap gap-4">
    {(
      [
        { value: 'soleTrader', label: 'Sole trader' },
        { value: 'company', label: 'Limited company' },
      ] as const
    ).map((o) => (
      <label
        key={o.value}
        className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500"
      >
        <input
          type="radio"
          checked={value === o.value}
          onChange={() => onChange(o.value)}
          className="h-[15px] w-[15px] accent-navy"
        />
        {o.label}
      </label>
    ))}
  </div>
);
